import _ from 'lodash';
import CountryUtils from './CountryUtils';
import DateUtils from './DateUtils';
import AddressTransformer from './AddressTransformer';
import FinancialDetailsTransformer from './Transformers/FinancialDetails';

const MappingUtils = {
  /**
   * Map all of the properties from the enquiry service response into our data store structure
   *  (new payload)
   *
   * @param  {Object} response    Response object from the enquiry service
   * @return {Array}            Array of data formatted to our data store structure
   */
  mapGetEnquiry(responseData, productType) {
    let adapter;
    const response = responseData;

    // getCustomerById doesn't have applicants array and all
    // response.personal_details turns into response.applicants[0]
    if (response.applicants) {
      const applicant = response.applicants.length && response.applicants[0];

      for (const key in applicant) {
        if (applicant.hasOwnProperty(key)) {
          response[key] = applicant[key];
        }
      }
    }

    const _isNotEmptyArray = (arr) => {
      return _.isArray(arr) && arr.length > 0;
    };

    /**
     * Returns eligibility questions mapping if available
     * @return {object} eligibility questions/answers
     */
    const _getEligibilityQuestions = () => {
      const eligibilityQuestionKeys = [
        'eligibilityAge18Question',
        'eligibilityUKResidentQuestion',
        'eligibilityDeviceQuestion',
      ];

      const eligibilityQuestions =
        response['audit_info'] &&
        response['audit_info']['eligibility_questions'];
      const result = {};

      // @ticket DYB-20878: compatibility with 0.6 release
      const positiveEligibilityQuestions = _.filter(eligibilityQuestions, {
        answer: 'Y',
      });

      if (positiveEligibilityQuestions) {
        _.each(positiveEligibilityQuestions, (value, index) => {
          result[eligibilityQuestionKeys[index]] = 'Yes';
        });
      }

      return result;
    };

    const _getEmploymentDetails = (key) => {
      return _isNotEmptyArray(response['employment_details'])
        ? response['employment_details'][0][key]
        : undefined;
    };

    const _getAddress = () => {
      if (_isNotEmptyArray(response['personal_details'].addresses)) {
        const addresses = AddressTransformer.fromGet(
          response['personal_details'].addresses
        );
        return !_.isEmpty(addresses) ? addresses : undefined;
      } else {
        return null;
      }
    };

    /**
     * Forms an account usage object
     * @return {Object}
     */
    const _getAccountUsage = () => {
      const accountUsageInformation = response['account_usage_info'];
      let result = {};

      _.each(accountUsageInformation, (item) => {
        const accountUsageFundsInfo = item['funds_info'];
        switch (item['usage_type']) {
          case 'CURRENT ACCOUNT':
            result = _.assign(result, {
              incomeOtherPaymentTypeOptions: accountUsageFundsInfo.source,
              incomeOtherPaymentAmount: parseInt(
                accountUsageFundsInfo.amount,
                10
              ),
              incomeOtherAccountPurpose: item.purpose,
            });
            break;

          case 'SAVINGS ACCOUNT':
            result = _.assign(result, {
              incomeOtherSavingsTypeOptions: accountUsageFundsInfo.source,
              incomeOtherSavingsFrequency: accountUsageFundsInfo.frequency,
              incomeOtherSavingsAmount: parseInt(
                accountUsageFundsInfo.amount,
                10
              ),
              incomeOtherSavingsPurpose: item.purpose,
            });

            break;

          default:
            break; // Do Nothing
        }
      });

      return result;
    };

    const _getContactDetails = (key) => {
      let result = response['contact_details']
        ? response['contact_details'][key]
        : undefined;

      // Some values may have been stored differently in older systems, e.g. Email -> EMAIL.
      if (result && result.toLowerCase) {
        switch (result.toLowerCase()) {
          case 'email':
            result = 'EMAIL';
            break;
          case 'mobile':
            result = 'PHONE';
            break;
          case 'mail':
            result = 'MAIL';
            break;
        }
      }

      return result;
    };

    /**
     * Returns a product code, falls back to IM136
     * @return {String} Product code
     */
    const _getProductCode = () => {
      return !response.products
        ? {}
        : {
            productCode: response.products && `IM${response.products.code}`,
          };
    };

    const _getBankInfo = () => {
      if (response['bank_info'] && _.isArray(response['bank_info'].accounts)) {
        return _.map(response['bank_info'].accounts, (account) => {
          return {
            accountType: account['account_type']
              ? account['account_type'].toLowerCase()
              : undefined,
            accountNumber: account['account_number'],
            sortCode: account['sort_code'],
          };
        });
      }

      return undefined;
    };

    const numberOfDependants = response['personal_details'][
      'number_of_dependents'
    ]
      ? parseInt(response['personal_details']['number_of_dependents'], 10)
      : 0;

    const getStatus = (residentialStatus, noMatch) =>
      residentialStatus && residentialStatus !== noMatch
        ? residentialStatus
        : '';

    adapter = {
      customerNumber: response['customer_number'],
      caseId: response['reference_id'],

      // Bank Info
      bankInfo: _getBankInfo(),

      // Personal Details
      gender: response['personal_details'].sex === 'M' ? 'Male' : 'Female',
      dateOfBirth: DateUtils.getDateStringFromAPI(
        response['personal_details'].dob
      ),
      maritalStatus: response['personal_details']['marital_status'],
      hasDependants: numberOfDependants > 0 ? 'Yes' : 'No',
      dependants:
        (!isNaN(numberOfDependants) && String(numberOfDependants)) || '0',
      title: response['personal_details'].name.title,
      firstName: response['personal_details'].name['first_name'],
      lastName: response['personal_details'].name['last_name'],
      hasMiddleName: response['personal_details'].name['middle_name']
        ? 'Yes'
        : 'No',
      middleName: response['personal_details'].name['middle_name'],

      // Address Details
      addresses: _getAddress(),

      residentialStatus: getStatus(
        response['personal_details']['residential_status'],
        'OTHER'
      ),

      // Contact Details
      phoneNumber:
        _getContactDetails('phone') && _getContactDetails('phone').mobile,
      emailAddress: _getContactDetails('email'),
      emailAddressConfirmation: _getContactDetails('email'),
      contactMethods:
        _getContactDetails('preferences') &&
        _.map(_getContactDetails('preferences').marketing, (value, key) => {
          if (value === 'Y' && key !== 'do_not_contact') {
            return key.toUpperCase();
          }

          return undefined;
        }),
      preferredContactMethod:
        _getContactDetails('preferences') &&
        _getContactDetails('preferences').contact.method,

      // Employment Details
      employmentStatus: _getEmploymentDetails('status'),
      employmentOccupation: _getEmploymentDetails('description'),
      employmentStartDate:
        _getEmploymentDetails('effective_period') &&
        DateUtils.getDateStringFromAPI(
          _getEmploymentDetails('effective_period').from
        ),
      employerName:
        _getEmploymentDetails('employer') &&
        _getEmploymentDetails('employer').name,

      // Nationality
      nationality: response['personal_details'].nationality,
      cityBorn: response['personal_details']['city_of_birth'],
      countryBorn: response['personal_details']['country_of_birth'],
    };

    adapter = _.extend(
      adapter,
      FinancialDetailsTransformer.fromGet(
        response['financial_details'],
        productType
      )
    );

    adapter = _.extend(
      adapter,
      _getAccountUsage(),
      _getEligibilityQuestions(),
      _getProductCode()
    );

    const lodashWrapper = _.runInContext();
    _.mixin(lodashWrapper, { chain: _.chain, map: _.map, filter: _.filter });

    return lodashWrapper
      .chain(adapter)
      .map((value, key) => {
        return { key, value };
      })
      .filter((item) => {
        return !_.isUndefined(item.value) && !_.isNull(item.value);
      })
      .value();
  },

  _deleteEmptyProperties(obj, isRecursive) {
    let isRecursiveValue = isRecursive;
    const objeData = obj;
    isRecursiveValue = isRecursiveValue || false;
    for (const i in objeData) {
      if (
        objeData[i] === null ||
        objeData[i] === undefined ||
        objeData[i] === ''
      ) {
        delete objeData[i];
      } else if (isRecursiveValue && typeof objeData[i] === 'object') {
        this._deleteEmptyProperties(objeData[i], isRecursiveValue);
      }
    }
  },

  /**
   * fetch current account data and return it's details
   * @param  {object} data InternetBanking data
   * @return {object}      Formatted object
   */
  _getNewAccountDetails(data) {
    const account =
      data &&
      data.bankInfo &&
      _.find(data.bankInfo, { accountType: 'current account' });

    return (
      account && {
        sort_code: account.sortCode,
        account_number: account.accountNumber,
      }
    );
  },

  /**
   * Build a CAS payload object.
   *
   * @param  {Object} data  From data store.
   * @return {Object}        To send.
   */
  mapSwitchPost(data) {
    const hasDebitCard = data.switchHasDebitCard === 'Yes';

    const result = {
      switch_type:
        data.switchType.toUpperCase().substr(0, 4) === 'FULL' ? 'FULL' : 'PART',
      customer_info: {
        nationality: CountryUtils.getCodeFromName(data.nationality),
        country_of_residence: 'GB', // A current UK address is required to signup.
      },
      new_account: this._getNewAccountDetails(data),
      old_account: {
        name: data.switchAccountName,
        debit_card: hasDebitCard,
        sort_code: data.switchSortCode,
        account_number: data.switchAccountNumber,
      },
    };

    // Optional fields.
    const switchDate = data.switchDate
      ? data.switchDate.split('-').reverse().join('')
      : undefined;
    if (switchDate) {
      result['complete_date'] = switchDate;
    }

    if (hasDebitCard) {
      result['old_account'].card = {
        number: data.switchDebitCardPan,
        expiry_date: data.switchDebitCardExpiration,
      };
    }

    return _.pickBy(result, (value) => {
      return value;
    });
  },
};

export default MappingUtils;
